import React, { useCallback, useEffect, useState } from "react";
import MobileCleanerHeader from "./MobileCleanerHeader";
import MobileBottomNav from "./MobileBottomNav";
import MobileFloorBar from "./MobileFloorBar";
import MobileCleanerCalendar from "./MobileCleanerCalendar";
import IcleanView from "../pages/IcleanView";
import { addDaysIso, useSwipeDays } from "./useSwipeDays";
import DeviceQrScanner from "./DeviceQrScanner";

const todayIso = () => new Date().toISOString().split("T")[0];

const MobileCleanerApp = ({ user, setUser, selectedCompany }) => {
  const [activeTab, setActiveTab] = useState("map");
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [routeIndex, setRouteIndex] = useState(0);
  const [routeItems, setRouteItems] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [shiftState, setShiftState] = useState("idle");
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [deviceLoginError, setDeviceLoginError] = useState(null);
  const [rootLocations, setRootLocations] = useState([]);
  const [activeRootLocationId, setActiveRootLocationId] = useState(null);
  const [showChat, setShowChat] = useState(false);

  const currentUserId = user?.user_id || user?.id;
  const companyId = selectedCompany?.id;

  useEffect(() => {
    if (!currentUserId || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const [locRes, settingsRes] = await Promise.all([
          fetch(`/locations?company_id=${companyId}`, { credentials: "include" }),
          fetch(`/canvas-settings?user_id=${currentUserId}&company_id=${companyId}`, { credentials: "include" }),
        ]);
        const locData = await locRes.json();
        if (cancelled) return;
        const roots = (locData.locations || []).filter((l) => l.is_root);
        setRootLocations(roots);
        let stored = null;
        try {
          const settingsData = await settingsRes.json();
          stored = settingsData?.settings?.active_root_location_id ?? null;
        } catch (_) {
          /* ignore */
        }
        const validStored = stored && roots.some((r) => String(r.id) === String(stored)) ? stored : null;
        setActiveRootLocationId(validStored ?? (roots.length === 1 ? roots[0].id : roots[0]?.id ?? null));
      } catch (err) {
        console.error("root locations:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserId, companyId]);

  const handleMobileRoutesLoaded = useCallback(({ items, loading }) => {
    setRouteItems(items || []);
    setRoutesLoading(!!loading);
  }, []);

  const changeDay = useCallback((delta) => {
    setSelectedDate((d) => addDaysIso(d, delta));
    setRouteIndex(0);
  }, []);

  const changeRoute = useCallback(
    (delta) => {
      setRouteIndex((idx) => {
        const len = routeItems.length;
        if (len <= 1) return idx;
        return (idx + delta + len) % len;
      });
    },
    [routeItems.length]
  );

  const routeSwipeHandlers = useSwipeDays(changeRoute);

  const activeRootLocation = rootLocations.find(
    (r) => String(r.id) === String(activeRootLocationId)
  );

  const openMapForDate = (iso) => {
    setSelectedDate(iso);
    setRouteIndex(0);
    setActiveTab("map");
  };

  const icleanViewProps = {
    selectedCompanyId: companyId,
    editMode: false,
    setEditMode: () => {},
    readerMode: false,
    setReaderMode: () => {},
    editLoctagMode: false,
    setEditLoctagMode: () => {},
    addMode: false,
    setAddMode: () => {},
    mopMode: false,
    setMopMode: () => {},
    drawMode: false,
    setDrawMode: () => {},
    cleanRouteMode: false,
    setCleanRouteMode: () => {},
    locationSettingsMode: false,
    setLocationSettingsMode: () => {},
    usersMode: false,
    user,
    cleanerMobileMode: true,
    mobileSelectedDate: selectedDate,
    mobileRouteIndex: routeIndex,
    onMobileRoutesLoaded: handleMobileRoutesLoaded,
    activeRootLocationId,
    registrationDevice: null,
    setRegistrationDevice: () => {},
    isReading: false,
    setIsReading: () => {},
    onSetRegistrationHandlers: () => {},
  };

  const handleTabChange = (tab) => {
    if (tab === "chat") {
      setShowChat(true);
      return;
    }
    setActiveTab(tab);
  };

  // On mount: check if the user already has an active device session
  useEffect(() => {
    fetch("/api/my-active-device-session", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.active && d.device_id) {
          setActiveDeviceId(d.device_id);
          setShiftState("working");
        }
      })
      .catch(() => {});
  }, []);

  const handleShiftToggle = async () => {
    if (shiftState === "working") {
      try {
        await fetch("/api/device/user-logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ device_id: activeDeviceId }),
          credentials: "include",
        });
      } catch (_) {}
      setShiftState("idle");
      setActiveDeviceId(null);
      return;
    }
    setDeviceLoginError(null);
    setShowQrScanner(true);
  };

  const handleQrScanned = async (deviceId) => {
    setShowQrScanner(false);
    try {
      const res = await fetch("/api/device/user-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId }),
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActiveDeviceId(deviceId);
        setShiftState("working");
      } else {
        setDeviceLoginError(data.detail || data.reason || "Sikertelen bejelentkezes");
      }
    } catch (_) {
      setDeviceLoginError("Halozati hiba");
    }
  };

  const handleBreak = () => {
    setShiftState((s) => (s === "break" ? "idle" : "break"));
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-900 overflow-hidden">
      <MobileCleanerHeader user={user} setUser={setUser} />

      {showQrScanner && (
        <DeviceQrScanner onScan={handleQrScanned} onClose={() => setShowQrScanner(false)} />
      )}

      {deviceLoginError && (
        <div className="fixed top-16 left-0 right-0 z-50 mx-4">
          <div className="bg-red-900/90 text-red-200 text-xs px-3 py-2 rounded flex justify-between items-center">
            <span>{deviceLoginError}</span>
            <button type="button" onClick={() => setDeviceLoginError(null)} className="ml-2 text-red-300">✕</button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 relative">
        {activeTab === "map" && (
          <>
            <MobileFloorBar
              rootLocations={rootLocations}
              activeRootLocationId={activeRootLocationId}
              setActiveRootLocationId={setActiveRootLocationId}
              selectedDate={selectedDate}
              onPrevDay={() => changeDay(-1)}
              onNextDay={() => changeDay(1)}
            />
            {/* Route list – swipe left/right to switch routes */}
            <div
              className="shrink-0 bg-slate-800 border-b border-slate-700 select-none"
              onTouchStart={routeSwipeHandlers.onTouchStart}
              onTouchEnd={routeSwipeHandlers.onTouchEnd}
            >
              {routesLoading && (
                <div className="px-3 py-2 text-xs text-slate-400">Betoltes...</div>
              )}
              {!routesLoading && routeItems.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">Nincs route erre a napra</div>
              )}
              {routeItems.map((item, idx) => {
                const active = idx === routeIndex;
                const time = item.plan?.scheduled_time;
                const code = item.route?.code;
                const name = item.route?.name;
                return (
                  <button
                    key={item.plan?.id || idx}
                    type="button"
                    onClick={() => setRouteIndex(idx)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-slate-700/50 last:border-b-0 ${
                      active ? "bg-teal-700/60 text-white" : "text-slate-300"
                    }`}
                  >
                    <span className={`shrink-0 font-mono w-11 text-right ${active ? "text-teal-200" : "text-slate-400"}`}>
                      {time || "––:––"}
                    </span>
                    <span className="flex-1 font-medium truncate">
                      {code ? `${code}` : ""}{code && name ? " – " : ""}{name || "Route"}
                    </span>
                    {activeRootLocation && (
                      <span className="shrink-0 text-[10px] text-slate-400 truncate max-w-[90px]">
                        {activeRootLocation.name}
                      </span>
                    )}
                    {active && routeItems.length > 1 && (
                      <span className="shrink-0 text-teal-400 text-[10px]">◀▶</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 min-h-0 relative">
              {activeRootLocation && (
                <div className="absolute top-1.5 left-0 right-0 z-10 flex justify-center pointer-events-none">
                  <span className="text-[11px] bg-black/50 text-white/90 px-3 py-0.5 rounded-full backdrop-blur-sm">
                    {activeRootLocation.name}
                  </span>
                </div>
              )}
              <IcleanView {...icleanViewProps} />
            </div>
          </>
        )}

        {activeTab === "calendar" && (
          <MobileCleanerCalendar
            rootLocations={rootLocations}
            activeRootLocationId={activeRootLocationId}
            setActiveRootLocationId={setActiveRootLocationId}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onOpenMapForDate={openMapForDate}
          />
        )}

        {activeTab === "menu" && (
          <div className="flex-1 p-4 text-slate-200 overflow-y-auto">
            <h2 className="text-lg font-bold mb-2">Menu</h2>
            <p className="text-sm text-slate-400 mb-4">{user?.name || user?.email}</p>
            <ul className="space-y-2 text-sm">
              <li className="p-3 bg-slate-800 rounded-lg">Profil (hamarosan)</li>
              <li className="p-3 bg-slate-800 rounded-lg">Beallitasok (hamarosan)</li>
              <li className="p-3 bg-slate-800 rounded-lg">
                Munkaallapot:{" "}
                {shiftState === "working" ? "Munkaban" : shiftState === "break" ? "Szunet" : "Keszenlet"}
              </li>
            </ul>
          </div>
        )}
      </div>

      {showChat && (
        <div className="absolute inset-0 z-[60] bg-black/50 flex items-end">
          <div className="w-full bg-slate-900 rounded-t-2xl p-4 max-h-[70vh] border-t border-slate-700">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-slate-100 font-semibold">Chat</h3>
              <button type="button" onClick={() => setShowChat(false)} className="text-slate-400 px-2">
                Bezaras
              </button>
            </div>
            <p className="text-sm text-slate-400">Uzenetek hamarosan.</p>
          </div>
        </div>
      )}

      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        shiftState={shiftState}
        onShiftToggle={handleShiftToggle}
        onBreak={handleBreak}
        onChat={() => setShowChat(true)}
      />
    </div>
  );
};

export default MobileCleanerApp;
